import { announce, AppMountError, element, mountShell, showShellError, type Shell } from "./ui/shell.ts";
import { bindPageLifecycle } from "./app/page-lifecycle.ts";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/shell.css";
import "./styles/controls.css";
import "./styles/panel.css";
import "./styles/responsive.css";
import "./styles/runtime.css";
import { safeMapAperture } from "./graph/safe-map-aperture.ts";

let mountedShell: Shell | null = null;

async function run(): Promise<void> {
  const root = document.getElementById("app");
  if (root === null) throw new AppMountError();
  const shell = mountShell(root);
  mountedShell = shell;
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  const [appModule, searchModule, loaderModule, stateModule, canvasModule, overviewModule, hybridModule, bindingsModule] = await Promise.all([
    import("./app/atlas-app.ts"),
    import("./data/atlas-search.ts"),
    import("./data/staged-loader.ts"),
    import("./domain/url-state.ts"),
    import("./graph/atlas-canvas-stack.ts"),
    import("./graph/overview-canvas-renderer.ts"),
    import("./graph/hybrid-atlas-renderer.ts"),
    import("./ui/atlas-bindings.ts"),
  ]);
  const { AtlasApp, InvalidAtlasLinkError } = appModule;
  const { AtlasSearch } = searchModule;
  const { StagedAtlasLoader } = loaderModule;
  const { parseAtlasUrl } = stateModule;
  const { mountCanvasStack, mountDetailCanvasStack } = canvasModule;
  const { OverviewCanvasRenderer } = overviewModule;
  const { browserHybridRendererOptions, HybridAtlasRenderer } = hybridModule;
  const { bindAtlasUi } = bindingsModule;
  const loader = new StagedAtlasLoader();
  const stack = mountCanvasStack(shell.graph);
  const canvas = new OverviewCanvasRenderer(stack, 1841);
  const renderer = new HybridAtlasRenderer(canvas, async () => {
    const { AtlasRenderer } = await import("./graph/atlas-renderer.ts");
    return new AtlasRenderer(mountDetailCanvasStack(shell.graph), 1841);
  }, browserHybridRendererOptions(window));
  const app = new AtlasApp(shell, loader, renderer);
  const search = new AtlasSearch(loader);
  await app.initialize();
  const stopBindings = bindAtlasUi({ app, shell, search, onError: (error) => reportInteraction(shell, error) });
  shell.setMapNavigationReady(true);
  const [minimumYear, maximumYear] = app.yearDomain();
  const parsed = parseAtlasUrl(location.search, { minimumYear, maximumYear });
  try {
    await app.restore(parsed);
  } catch (error) {
    if (!(error instanceof InvalidAtlasLinkError)) throw error;
    await app.restore({ kind: "invalid", message: error.message });
  }
  app.finishRestore();
  Object.defineProperty(window, "__atlasQa", {
    configurable: true,
    value: {
      state: () => app.snapshot(),
      semanticState: () => app.semanticSnapshot(),
      camera: () => renderer.cameraState(),
      graph: () => ({ nodes: renderer.graph().order, edges: renderer.graph().size, ids: renderer.graph().nodes() }),
      scene: () => {
        const graph = renderer.graph();
        const bridgeSubstituted = renderer.bridgeSubstitutedEdges?.() ?? new Set<string>();
        const bridgeSample = renderer.bridgeSample?.() ?? { flows: [], ports: [] };
        const nodes = graph.nodes().map((id) => {
          const node = graph.getNodeAttributes(id);
          const viewport = renderer.viewportPosition(id);
          const viewportRadius = renderer.viewportRadius(id) ?? 0;
          return {
            id, kind: node.kind, role: node.role, parentId: node.parentId, semanticId: node.semanticId,
            label: node.label, hidden: node.hidden, displayed: !node.hidden && node.role !== "extent-anchor" && viewportRadius > 0,
            viewport, viewportRadius,
          };
        });
        for (const port of bridgeSample.ports ?? []) {
          const index = nodes.findIndex((node) => node.id === port.id);
          const diagnostic = {
            id: port.id, kind: "meso" as const, role: "portal" as const,
            parentId: port.remoteMacroId ?? null, semanticId: port.remoteMesoId ?? port.id,
            label: port.label, hidden: false, displayed: true, viewport: port.point, viewportRadius: port.hitRadius,
          };
          if (index < 0) nodes.push(diagnostic);
          else nodes[index] = diagnostic;
        }
        const edges = graph.edges().map((id) => {
          const edge = graph.getEdgeAttributes(id);
          const source = graph.source(id);
          const target = graph.target(id);
          const endpointsVisible = !graph.getNodeAttribute(source, "hidden") && !graph.getNodeAttribute(target, "hidden");
          return {
            id, source, target, hidden: edge.hidden,
            displayed: renderer.graphEdgeDisplayed?.(id) ?? (endpointsVisible && edge.bridge !== true && !edge.hidden && edge.coarseHidden !== true && !bridgeSubstituted.has(id)),
            bridgeSubstituted: bridgeSubstituted.has(id),
            coarseHidden: edge.coarseHidden ?? false, priority: edge.priority, topology: edge.topology,
            remotePortId: edge.remotePortId, remoteMesoId: edge.remoteMesoId,
            remoteMacroId: edge.remoteMacroId, remoteJudgmentId: edge.remoteJudgmentId,
          };
        });
        bridgeSample.flows.forEach((flow, index) => {
          const remoteIsSource = flow.remoteEndpoint === "source";
          edges.push({
            id: `canvas-bridge:${index}:${flow.sourceId}:${flow.targetId}`,
            source: remoteIsSource ? flow.remotePortId : flow.sourceId,
            target: remoteIsSource ? flow.targetId : flow.remotePortId,
            hidden: false, displayed: true, bridgeSubstituted: false, coarseHidden: false,
            priority: "focused-boundary", topology: flow.relationship.resolution === "exact" ? "exact-port" : "aggregate",
            remotePortId: flow.remotePortId, remoteMesoId: flow.relationship.remoteMesoId,
            remoteMacroId: flow.relationship.remoteMacroId, remoteJudgmentId: flow.relationship.remoteJudgmentId,
          });
        });
        return {
          completeDisplayedEdges: true,
          renderedLayers: ["graphology", "canvas-bridge"],
          nodes,
          edges,
        };
      },
      safeAperture: () => renderer.safeAperture?.(24) ?? safeMapAperture(
        { width: shell.graph.clientWidth, height: shell.graph.clientHeight },
        matchMedia("(pointer: coarse)").matches,
        24,
      ),
      performance: () => renderer.performanceDiagnostics(),
      bridgeEdges: () => renderer.graph().filterEdges((_key, edge) => edge.bridge === true).length,
      bridgeEndpoints: () => renderer.graph().filterEdges((_key, edge) => edge.bridge === true).slice(0, 16).map((key) => {
        const graph = renderer.graph();
        const endpoint = (id: string) => {
          const node = graph.getNodeAttributes(id);
          return { id, x: node.x, y: node.y, role: node.role, semanticId: node.semanticId, parentId: node.parentId };
        };
        return { source: endpoint(graph.source(key)), target: endpoint(graph.target(key)) };
      }),
      point: (id: string) => renderer.viewportPosition(id),
      activate: (id: string) => app.activateNode(id),
      select: (ecli: string) => app.selectEcli(ecli),
      domain: () => app.yearDomain(),
      renderer: () => renderer.backendKind(),
      alignment: () => renderer.handoffAlignmentDelta(),
    },
  });
  bindPageLifecycle(window, () => {
    try {
      stopBindings();
    } finally {
      app.dispose();
    }
  });
}

function reportInteraction(shell: Shell, error: Error): void {
  if (error instanceof DOMException && error.name === "AbortError") return;
  console.error(error);
  announce(shell, `Handeling mislukt: ${error.message}. De kaart blijft beschikbaar.`);
}

function reportFatal(error: unknown): void {
  const fatal = error instanceof Error ? error : new TypeError(String(error));
  console.error(fatal);
  if (mountedShell !== null) {
    showShellError(mountedShell, fatal);
    return;
  }
  document.body.append(element("div", { class: "fatal-error", role: "alert" }, [`Laden mislukt: ${fatal.message}`]));
}

void run().catch(reportFatal);
